import {
  Card,
  CardBody,
  CardHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  SimpleGrid,
  Stack,
  StackDivider,
  Text,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery, useFetchUsersPageQuery } from 'api';
import { PostPurchaseOrderRequest } from 'api/bindings/PostPurchaseOrderRequest';
import { PostPurchaseOrderTransactionRequest } from 'api/bindings/PostPurchaseOrderTransactionRequest';
import { PutPurchaseOrderRequest } from 'api/bindings/PutPurchaseOrderRequest';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import { CurrencyLabel, FormButtonGroup, UserLink } from 'features/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

type PurchaseOrderModel = PostPurchaseOrderRequest | PutPurchaseOrderRequest;

function isPostRequest(
  dto: PurchaseOrderModel
): dto is PostPurchaseOrderRequest {
  return !('id' in dto);
}
function isPutRequest(dto: PurchaseOrderModel): dto is PutPurchaseOrderRequest {
  return 'id' in dto;
}
export interface PurchaseOrderFormReviewDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  po: PurchaseOrderModel;
  afeId: string | undefined;
  revision: PostPurchaseOrderTransactionRequest | undefined;
  onSubmit: (form: Form) => void;
  onCancel: () => void;
}

interface Form {}

export const PurchaseOrderFormReviewDrawerContent = (
  props: PurchaseOrderFormReviewDrawerContentProps
) => {
  const { po, revision, afeId = '0', onSubmit, onCancel, ...rest } = props;
  const form = useForm<Form>({ mode: 'all' });
  const { handleSubmit, formState } = form;

  // Fetches
  const auth = useAuth();
  const { data: afe } = useFetchAfeDetailsQuery(afeId, { skip: !afeId });
  const { data: users } = useFetchUsersPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );
  const liCount = useMemo(
    () => (isPutRequest(po) ? po?.lineItems?.length ?? 0 : 0),
    [po]
  );
  const orderSum = useMemo(
    () =>
      // sum(
      //   po.lineItems.map((li) =>
      //     new Decimal(li.unitPrice).mul(new Decimal(li.quantityOrdered))
      //   )
      // ),
      isPutRequest(po)
        ? po?.lineItems?.reduce(
            (sum, n) =>
              sum.add(
                new Decimal(n.unitPrice || '0').mul(
                  new Decimal(n.quantityOrdered || '0')
                )
              ),
            new Decimal(0)
          ) ?? new Decimal(0)
        : new Decimal(0),
    [po]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Review Changes</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <Card>
                <CardHeader>Purchase Order: {po.name}</CardHeader>
                <CardBody>
                  <Stack spacing={4} divider={<StackDivider />}>
                    <SimpleGrid columns={2} gap={4}>
                      <Text fontSize={'sm'} pt={2}>
                        AFE:
                      </Text>
                      <Text>{afe?.name}</Text>
                      <Text fontSize={'sm'} pt={2}>
                        Line Items:
                      </Text>
                      <Text>{liCount}</Text>
                      <Text fontSize={'sm'} pt={2}>
                        Total:
                      </Text>
                      <CurrencyLabel value={orderSum} />
                      <Text fontSize={'sm'} pt={2}>
                        Asignee:
                      </Text>
                      <UserLink
                        u={users?.items.find((x) => x.id === po.assigneeUserId)}
                      />
                    </SimpleGrid>
                  </Stack>
                </CardBody>
              </Card>
              {revision && (
                <Card>
                  <CardHeader>Revision: {revision.name}</CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={4}>
                      <Text fontSize={'sm'} pt={2}>
                        Name:
                      </Text>
                      {revision.name}
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              disabledWhenClean={false}
              onCancel={onCancel}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
